import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import type { TFunction } from "i18next";
import { Tag } from "lucide-react";
import { useFeatureValue } from "@growthbook/growthbook-react";
import { topbarPillBaseClasses } from "@/components/layout/balance-button";
import { useSpecialOffers } from "@/features/subscription/payment-queries";
import { useSubscription } from "@/features/subscription/queries";
import { isActivePaidSubscriber } from "@/features/subscription/utils/subscription-status";
import { useAuth } from "@/hooks/use-auth";
import { usePermissions } from "@/hooks/use-permissions";
import { trackFunnel } from "@/lib/integrations/sentry-tracking";
import { pushDataLayer } from "@/lib/integrations/ga4";
import { useSpecialOfferExperiment } from "@/features/special-offers/use-special-offer-experiment";
import type { Offer } from "@/features/subscription/types";

const PILL_COPY_FALLBACK: Record<"B" | "C" | "D", string> = {
  B: "Unlock {{percent}}% off now",
  C: "Ends soon {{percent}}% off",
  D: "Claim {{percent}}% savings",
};

function getPillLabel(
  t: TFunction,
  discountPercentage: number,
  copyArm: string,
): string {
  if (
    discountPercentage > 0 &&
    (copyArm === "B" || copyArm === "C" || copyArm === "D")
  ) {
    return t(`TOPBAR.POWERSPOT.SPECIAL_OFFER.PILL_${copyArm}`, {
      defaultValue: PILL_COPY_FALLBACK[copyArm],
      percent: discountPercentage,
    });
  }
  if (discountPercentage > 0) {
    return t("TOPBAR.POWERSPOT.SPECIAL_OFFER.LABEL_SAVE", {
      defaultValue: "Save {{percent}}% on your deal",
      percent: discountPercentage,
    });
  }
  return t("TOPBAR.POWERSPOT.SPECIAL_OFFER.LABEL", {
    defaultValue: "Claim your deal",
  });
}

export function SpecialOfferPill() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { canManageBilling } = usePermissions();
  const { data: subscription } = useSubscription();
  const { data: offers, isLoading } = useSpecialOffers();

  if (isLoading) return null;
  if (!canManageBilling) return null;
  if (isActivePaidSubscriber(subscription)) return null;
  // Business accounts (same signal book-a-call uses) get the sales call, not a discount.
  if (user?.salesQualified === true) return null;
  if (pathname === "/special-offers") return null;
  if (!offers || offers.length === 0) return null;

  // Enrollment lives in the child so useFeatureValue fires only for offer-eligible
  // users; offers.length is runtime data a GrowthBook condition cannot express.
  return <EligibleSpecialOfferPill offers={offers} />;
}

function EligibleSpecialOfferPill({ offers }: { offers: Offer[] }) {
  const { inHoldout, arm } = useSpecialOfferExperiment(true);
  if (inHoldout) return null;
  return <VisibleSpecialOfferPill offer={offers[0]} offerArm={arm} />;
}

// Mounts only for non-holdout users, so the pill-copy experiment enrolls only pill-seers.
function VisibleSpecialOfferPill({ offer, offerArm }: { offer: Offer | undefined; offerArm: string }) {
  const { t } = useTranslation();
  const copyArm = useFeatureValue("special_offer_pill_copy", "A");
  const safeCopyArm = copyArm === "B" || copyArm === "C" || copyArm === "D" ? copyArm : "A";
  const impressionRef = useRef(false);

  const discountPercentage = offer?.pricing?.discountPercentage ?? 0;
  const pillLabel = getPillLabel(t, discountPercentage, safeCopyArm);

  const offerParams = {
    offer_id: offer?.offerId,
    offer_title: (offer?.title || offer?.name)?.slice(0, 80),
    offer_arm: offerArm,
    pill_copy_arm: safeCopyArm,
  };

  useEffect(() => {
    if (impressionRef.current) return;
    impressionRef.current = true;
    pushDataLayer("SpecialOffer", "PillImpression", offer?.offerId, undefined, {
      offer_id: offer?.offerId,
      offer_title: (offer?.title || offer?.name)?.slice(0, 80),
      offer_arm: offerArm,
      pill_copy_arm: safeCopyArm,
    });
  }, [offer, offerArm, safeCopyArm]);

  return (
    <Link
      to="/special-offers"
      state={{ pillCopyArm: safeCopyArm }}
      onClick={() => {
        trackFunnel("powerspot", "special_offer_pill_clicked", { pill_copy_arm: safeCopyArm });
        pushDataLayer("SpecialOffer", "PillClick", offer?.offerId, undefined, offerParams);
      }}
      className={`${topbarPillBaseClasses} border-primary/30 bg-primary/5 text-primary/80 hover:bg-primary/10`}
      aria-label={pillLabel}
      data-testid="topbar-special-offer"
    >
      <Tag className="h-3.5 w-3.5" aria-hidden="true" />
      <span className="hidden xl:inline font-medium">{pillLabel}</span>
    </Link>
  );
}
